import React, { useContext, useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import PropTypes from "prop-types";
import { AuthContext } from "../context/AuthContext";
import { SERVER_BASE_URL } from "../config";
import Spinner from './Spinner';

const ProtectedRoute = ({ element, allowedRoles, requireSubscription = false }) => {
  const { isInitializing, isAuthenticated, user } = useContext(AuthContext);
  const [hasAccess, setHasAccess] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(false);

  useEffect(() => {
    // Если подписка не требуется или пользователь админ - даем доступ
    if (!requireSubscription || (user && user.role === 'admin')) {
        setHasAccess(true);
        return;
    }
    
    // Если требуется подписка но пользователь не аутентифицирован
    if (requireSubscription && !isInitializing && !isAuthenticated) {
        setHasAccess(false);
        return;
    }
    
    // Ждем окончания инициализации
    if (isInitializing || !user) {
        return;
    }

    // Для всех пользователей (включая воркеров) используем API эндпоинт
    // который уже содержит логику проверки подписки организатора для воркеров
    const checkSubscriptionStatus = async () => {
        setCheckingSubscription(true);
        const token = localStorage.getItem('token');
        
        try {
            // console.log(`Проверка подписки для пользователя ${user.email} (роль: ${user.role})`);
            
            const response = await fetch(`${SERVER_BASE_URL}/api/subscription/status`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`Ошибка проверки подписки: ${errorText}`);
                setHasAccess(false);
            } else {
                const contentType = response.headers.get("content-type") || "";
                if (!contentType.includes("application/json")) {
                    const nonJsonBody = await response.text();
                    console.error("Subscription check returned non-JSON response:", nonJsonBody);
                    setHasAccess(false);
                    return;
                }

                const data = await response.json();
                const hasSubscriptionAccess =
                  typeof data.has_access === "boolean"
                    ? data.has_access
                    : Boolean(data.is_subscription_active || data.is_trial_active);
                setHasAccess(hasSubscriptionAccess);
            }
        } catch (error) {
            console.error('Ошибка при проверке подписки:', error);
            setHasAccess(false);
        } finally {
            setCheckingSubscription(false);
        }
    };

    checkSubscriptionStatus();

  }, [isInitializing, isAuthenticated, user, requireSubscription]);

  // Пока идет инициализация или проверка, показываем загрузку
  if (isInitializing || checkingSubscription || hasAccess === null) {
    return (
        <div className="flex justify-center items-center h-screen">
            <Spinner className="w-12 h-12" />
        </div>
    );
  }

  // Если не аутентифицирован, редирект на логин
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Проверка ролей (если указаны)
  if (
    allowedRoles &&
    !allowedRoles.map((role) => role.toLowerCase()).includes((user.role || "").toLowerCase())
  ) {
    return <Navigate to="/unauthorized" />;
  }

  // Проверка подписки (если требуется и нет доступа)
  if (requireSubscription && !hasAccess && user.role !== 'admin') {
    console.log('Доступ запрещен из-за отсутствия подписки. Редирект на /subscription.');
    return <Navigate to="/subscription" />;
  }

  // Если все проверки пройдены
  return element;
};

ProtectedRoute.propTypes = {
  element: PropTypes.element.isRequired,
  allowedRoles: PropTypes.array,
  requireSubscription: PropTypes.bool
};

export default ProtectedRoute;
